/**
 * arbi-dev Sidebar. Write-from-scratch replacement for arbi root's 768-line
 * Sidebar — arbi-dev needs a flat 13-item list with no badge counts, no
 * realtime channels, no role filtering (admin-only as a unit), no
 * expand/collapse per item, no Help overlay, no buying-session entry.
 *
 * Bottom of the sidebar carries a "← Back to Arbi" cross-subdomain link
 * so admins can hop back to arbi without typing a URL.
 */
import { type JSX, type ReactNode, useState, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Inbox,
  ListTodo,
  Map,
  Sliders,
  Users,
  GitCommit,
  AlertCircle,
  Database,
  FileText,
  Megaphone,
  Activity,
  CalendarRange,
  ListChecks,
  ArrowLeft,
  type LucideIcon,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';
import { useBadgeCount } from '../../hooks/useBadgeCount';

interface NavEntry {
  id: string;
  label: string;
  icon: LucideIcon;
  to: string;
}

const NAV: NavEntry[] = [
  { id: 'feedback', label: 'Feedback Inbox', icon: Inbox, to: '/admin/feedback' },
  { id: 'backlog', label: 'Backlog Manager', icon: ListTodo, to: '/admin/backlog' },
  { id: 'engine-atlas', label: 'Engine Atlas', icon: Map, to: '/admin/engine-atlas' },
  { id: 'engine-editor', label: 'Engine Editor', icon: Sliders, to: '/admin/engine-editor' },
  { id: 'users', label: 'User Management', icon: Users, to: '/admin/users' },
  { id: 'builds', label: 'Build Tracker', icon: GitCommit, to: '/admin/builds' },
  { id: 'errors', label: 'Error Log Viewer', icon: AlertCircle, to: '/admin/errors' },
  { id: 'algopix', label: 'Algopix Job Monitor', icon: Database, to: '/admin/algopix-jobs' },
  { id: 'docs', label: 'Docs CMS', icon: FileText, to: '/admin/docs' },
  { id: 'releases', label: 'Release Notes Manager', icon: Megaphone, to: '/admin/releases' },
  { id: 'health', label: 'System Health Dashboard', icon: Activity, to: '/admin/health' },
  { id: 'digest', label: 'Weekly Digest', icon: CalendarRange, to: '/admin/digest' },
  { id: 'runs', label: 'Job Runs', icon: ListChecks, to: '/admin/runs' },
];

const ICON_SIZE = 18;
const ARBI_DASHBOARD_URL = 'https://arbi.ironcladsupply.co/dashboard';

// Mirror of arbi-root's Sidebar CountBadge primitive (Sidebar.tsx
// L189-207). Stays inline — leaf component, not the duplicated logic.
// The fetch + Realtime channel pattern was promoted Day 63b to the
// shared `useBadgeCount` hook (apps/arbi-dev/src/hooks/useBadgeCount.ts),
// consumed twice below for /admin/feedback and /admin/errors.
function CountBadge({ count }: { count: number }): JSX.Element | null {
  if (count <= 0) return null;
  return (
    <span
      className={clsx(
        'ml-auto min-w-[1.25rem] h-[1.125rem] rounded-full bg-accent/20',
        'text-2xs text-accent font-mono flex items-center justify-center px-1 leading-none'
      )}
    >
      {count}
    </span>
  );
}

interface TooltipProps {
  text: string;
  children: ReactNode;
}

function Tooltip({ text, children }: TooltipProps): JSX.Element {
  const [show, setShow] = useState<boolean>(false);
  const [pos, setPos] = useState<{ top: number }>({ top: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);

  const handleEnter = (): void => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPos({ top: rect.top + rect.height / 2 });
    }
    setShow(true);
  };

  return (
    <div
      ref={triggerRef}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setShow(false)}
      className="relative"
    >
      {children}
      {show && (
        <div
          className="fixed z-50 ml-2 px-2 py-1 rounded bg-surface-4 border border-border text-xs text-ink-bright whitespace-nowrap pointer-events-none shadow-lg"
          style={{ left: 56, top: pos.top, transform: 'translateY(-50%)' }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

export default function Sidebar(): JSX.Element {
  const { sidebarCollapsed, density } = useApp();
  const location = useLocation();
  const collapsed = sidebarCollapsed;
  const py = density === 'comfortable' ? 'py-2' : 'py-1.5';

  // ── Live badge counts via shared useBadgeCount hook ─────────
  // Day 63b promotion. Two consumers fire the rule-of-three trigger:
  // /admin/feedback (existing, refactored from inline pattern) +
  // /admin/errors (new, Day 63b Error Log Viewer surface).
  const feedbackCount = useBadgeCount({
    table: 'feedback_items',
    filter: { status: 'open' },
    channelName: 'arbi-dev-feedback-badge',
  });
  const errorCount = useBadgeCount({
    table: 'sentry_fingerprints',
    filter: { status: 'open' },
    channelName: 'arbi-dev-errors-badge',
  });

  const badgeCount = (path: string): number => {
    if (path === '/admin/feedback') return feedbackCount;
    if (path === '/admin/errors') return errorCount;
    return 0;
  };

  const badgeAriaLabel = (item: NavEntry, count: number): string => {
    if (count <= 0) return item.label;
    const noun = item.id === 'errors' ? 'open errors' : 'open items';
    return `${item.label} — ${count} ${noun}`;
  };

  const handleBackToArbi = (): void => {
    if (typeof window !== 'undefined') {
      if (import.meta.env.PROD) {
        window.location.assign(ARBI_DASHBOARD_URL);
      } else {
        console.warn(
          '[arbi-dev/Sidebar] Would navigate to ' + ARBI_DASHBOARD_URL + ' (dev mode — skipping)'
        );
      }
    }
  };

  const isActive = (path: string): boolean => location.pathname === path;

  return (
    <aside
      className={clsx(
        'flex flex-col h-full bg-sidebar transition-all duration-200 flex-shrink-0 overflow-hidden',
        collapsed ? 'w-[52px]' : 'w-[220px]'
      )}
    >
      <nav
        className="sidebar-nav flex-1 overflow-y-auto pl-2 pr-2.5 pt-3 space-y-0.5 pb-2"
        style={{ minHeight: 0 }}
      >
        {NAV.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.to);

          const count = badgeCount(item.to);
          const ariaLabel = badgeAriaLabel(item, count);

          if (collapsed) {
            return (
              <div key={item.id}>
                <Tooltip text={ariaLabel}>
                  <NavLink
                    to={item.to}
                    className={clsx(
                      'w-full flex items-center justify-center rounded-md cursor-pointer transition-colors duration-100 h-9',
                      active
                        ? 'bg-accent-muted text-accent'
                        : 'text-ink-secondary hover:bg-surface-4 hover:text-ink-primary'
                    )}
                    aria-label={ariaLabel}
                  >
                    <Icon size={ICON_SIZE} />
                  </NavLink>
                </Tooltip>
              </div>
            );
          }

          return (
            <NavLink
              key={item.id}
              to={item.to}
              aria-label={ariaLabel}
              className={clsx(
                'flex items-center gap-3 px-3 rounded-md border-l-2 text-sm cursor-pointer transition-colors duration-100 select-none',
                py,
                active
                  ? 'border-accent bg-accent-muted text-accent'
                  : 'border-transparent text-ink-secondary hover:bg-surface-4 hover:text-ink-primary'
              )}
            >
              <Icon size={ICON_SIZE} className="flex-shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
              <CountBadge count={count} />
            </NavLink>
          );
        })}
      </nav>

      <div className="px-2 py-2 flex-shrink-0 border-t border-border-subtle">
        {collapsed ? (
          <Tooltip text="Back to Arbi">
            <button
              onClick={handleBackToArbi}
              className="w-full flex items-center justify-center h-9 rounded-md text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
              aria-label="Back to Arbi"
            >
              <ArrowLeft size={ICON_SIZE} />
            </button>
          </Tooltip>
        ) : (
          <button
            onClick={handleBackToArbi}
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-2xs text-ink-secondary hover:bg-surface-4 hover:text-ink-primary transition-colors cursor-pointer"
            aria-label="Back to Arbi"
          >
            <ArrowLeft size={14} className="flex-shrink-0" />
            <span className="truncate">Back to Arbi</span>
          </button>
        )}
      </div>
    </aside>
  );
}
